/**
 * Up to two initials for an avatar, from a display name.
 *
 * `Ada Lovelace` -> `AL`, `Ada` -> `A`, `ada van der berg` -> `AV`.
 *
 * Existed character-for-character in the sidebar's user chip and on the account
 * page, and the sidebar port was about to make it a third copy.
 *
 * The fallback chain is not decoration: a display name can be a single
 * character, an emoji, or — for an account created from an email with no
 * profile — whitespace. Every branch here is a name someone actually has.
 */
export function initialsOf(displayName: string | undefined | null): string {
  /* Trimmed first, which neither of the copies this replaces did. A name of
     nothing but whitespace fell through the word split to an empty result, then
     through the fallback to `name[0]` — which is a space, and a space is truthy,
     so the avatar rendered blank instead of '?'. */
  const name = displayName?.trim();
  if (!name) return '?';

  const fromWords = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => firstCharacter(part))
    .join('');
  return fromWords || firstCharacter(name) || '?';
}

/* `[0]` on a string is the first UTF-16 code unit, not the first character, and
   an emoji is two of them. `'🙂'[0]` is a lone high surrogate: a truthy 1-length
   string, so it satisfied `fromWords ||` and the '?' fallback never ran — the
   avatar rendered a replacement glyph rather than the emoji or the fallback.
   Spreading iterates code points instead. */
function firstCharacter(value: string): string {
  return [...value][0]?.toUpperCase() ?? '';
}
